const NESTED_ERROR_KEYS = ['response', 'data', 'details', 'innerError', 'error'] as const;
const MAX_ERROR_DEPTH = 8;

interface ICollectedErrorDetails {
  messages: string[];
  codes: string[];
}

export interface IExtractedErrorDetails {
  message?: string;
  code?: string;
  messages: string[];
  codes: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const addUnique = (values: string[], value: string): void => {
  if (value.trim() && !values.includes(value)) values.push(value);
};

const collectErrorDetails = (value: unknown, details: ICollectedErrorDetails, seen: Set<object>, depth: number): void => {
  if (depth > MAX_ERROR_DEPTH || value === null || value === undefined) return;

  if (typeof value === 'string') {
    addUnique(details.messages, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => collectErrorDetails(entry, details, seen, depth + 1));
    return;
  }

  if (!isRecord(value) || seen.has(value)) return;
  seen.add(value);

  for (const key of NESTED_ERROR_KEYS) {
    if (Object.hasOwn(value, key)) collectErrorDetails(value[key], details, seen, depth + 1);
  }

  if (Object.hasOwn(value, 'code') && typeof value.code === 'string') {
    addUnique(details.codes, value.code);
  }

  if (Object.hasOwn(value, 'message')) {
    collectErrorDetails(value.message, details, seen, depth + 1);
  }
};

export const extractErrorDetails = (error: unknown): IExtractedErrorDetails => {
  const details: ICollectedErrorDetails = { messages: [], codes: [] };
  collectErrorDetails(error, details, new Set<object>(), 0);

  return {
    message: details.messages[0],
    code: details.codes[0],
    messages: details.messages,
    codes: details.codes,
  };
};

/**
 * Extrae el primer mensaje legible de cualquier envelope de error conocido.
 * Los objetos sin `message` no se convierten a string: así nunca llega
 * `[object Object]` a una notificación.
 */
export const extractErrorMessage = (error: unknown): string => extractErrorDetails(error).message ?? '';

export const isDirectTranslationKey = (message: string): boolean =>
  message.startsWith('common.') || (message.includes('.') && !message.includes(' '));
